// Client-side orchestration of the LEGEND fermentation swap/start sequence.
// Verify-then-advance: every step is confirmed against breweryoverview device
// state before the next command fires. MQTT notifications only trigger an
// immediate re-poll; polling is the source of truth.
import * as sessionService from "./sessionService";
import * as breweryService from "./breweryService";
import mqttService from "@monitor/mqttService";
import PROCESS_TYPES from "@common/constants/processTypes";
import { USER_ACTION_IDS } from "@common/constants/useractionConstants";
import analyticsService, {
  ANALYTICS_TYPES,
} from "@common/services/analyticsService";

// The ONLY gates this loop is allowed to auto-ACK. The keg cannot begin
// fermenting without these two, and the portal user is standing at their own
// machine when they fire it — see the module-level note above
// _autoAckUntilSettled. Anything else (mount tap 10, connect pressurizer 31,
// check carbonation 65, ...) is a serving-handoff or physical-confirmation
// gate a person must action themselves; auto-ACKing one from here would
// assert a physical event nobody performed. This is enforced by construction,
// not by timing — even if a keg raises an onward gate FIRST (e.g. it still
// held gate 31 from a previous run), this loop will never touch it.
const AUTO_ACK_ALLOWED_GATES = [
  USER_ACTION_IDS.ACTION_PREPARE_FERMENTATION,
  USER_ACTION_IDS.ACTION_DO_NOT_MOVE_KEG,
  USER_ACTION_IDS.ACTION_PLACE_AIRLOCK,
  USER_ACTION_IDS.ACTION_PLACE_TRUB_CONTAINER,
  USER_ACTION_IDS.ACTION_PLACE_BLOW_OFF,
];

export const PHASES = {
  IDLE: "idle",
  STOPPING: "stopping",
  AWAIT_IDLE: "await-idle",
  STARTING: "starting",
  ACKING: "acking",
  DONE: "done",
  ERROR: "error",
};

const INTENT_TTL_MS = 24 * 60 * 60 * 1000;

export const intentKey = (deviceUuid) => `fermentationIntent:${deviceUuid}`;

// Device uuids with an orchestration currently running in THIS tab. The
// brewery page's resume-banner detection consults it so a freshly-saved
// intent doesn't surface a banner for an operation the user is watching in
// the modal (or just resumed).
export const activeOrchestrations = new Set();

// request.js's handleResponse rejects with one of three shapes:
//   - a Promise of the parsed JSON error body (`throw res.json()`) for any
//     HTTP >= 400 response with a JSON content-type,
//   - an Error(statusText) for non-JSON error responses,
//   - the literal string "Network error, please try again later.".
// The session-not-active rejection arrives as the thenable shape with a body
// containing one of these phrases. We match the BODY (not just the shape) so a
// JSON-bodied 500/403 isn't silently swallowed as "already stopped"; anything
// unmatched rethrows. Phrases seen from the backend when STOP can't apply:
//   - "an active session is required" / "Invalid command_type"
//   - "Can't send this command with the current session state." — the 400 a STOP
//     gets when the keg device is already idle (e.g. a session that diverged from
//     the device after a sim/firmware restart). Treating it as already-stopped lets
//     the swap fall through to await-idle, which is the real source of truth.
const ALREADY_STOPPED_RE =
  /active session|invalid command|current session state/i;

async function isAlreadyStoppedError(err) {
  if (err && typeof err.then === "function") {
    let body;
    try {
      body = await err; // drain so the body promise never goes unhandled
    } catch (e) {
      return false; // body unreadable — assume nothing, rethrow upstream
    }
    return ALREADY_STOPPED_RE.test(JSON.stringify(body));
  }
  const text = typeof err === "string" ? err : (err && err.message) || "";
  return ALREADY_STOPPED_RE.test(text);
}

// Pull a human-readable string out of whatever request.js rejected with (see the
// rejection-shapes note above isAlreadyStoppedError) and classify it. kind ===
// "session-conflict" for the busy-keg 400 so the modal can offer the
// stop-and-retry recovery.
export async function normalizeError(err) {
  let body = err;
  if (err && typeof err.then === "function") {
    try {
      body = await err;
    } catch (e) {
      body = null;
    }
  }
  const message = extractErrorMessage(body) || "The keg did not respond.";
  const kind = /used by another session/i.test(message)
    ? "session-conflict"
    : "other";
  return { message, kind };
}

function extractErrorMessage(body) {
  if (!body) return null;
  if (typeof body === "string") return body;
  if (body instanceof Error) return body.message;
  if (typeof body === "object") {
    if (typeof body.detail === "string") return body.detail;
    if (Array.isArray(body.non_field_errors) && body.non_field_errors.length) {
      return body.non_field_errors[0];
    }
    // First field whose value is a non-empty string array (DRF field errors),
    // e.g. { keg_uuid: ["The device is currently being used by another session"] }.
    const keys = Object.keys(body);
    for (let i = 0; i < keys.length; i++) {
      const v = body[keys[i]];
      if (Array.isArray(v) && v.length && typeof v[0] === "string") return v[0];
      if (typeof v === "string" && v) return v;
    }
  }
  return null;
}

export class FermentationOrchestrator {
  constructor({
    deviceUuid,
    sessionId,
    mode,
    settings,
    beerRecipeId,
    onPhase,
    pollIntervalMs,
    timeoutMs,
    ackTimeoutMs,
    startTimeoutMs,
    stepDelayMs,
  }) {
    this.deviceUuid = deviceUuid;
    this.sessionId = sessionId;
    this.mode = mode; // "swap" | "start"
    this.settings = settings;
    this.beerRecipeId = beerRecipeId != null ? beerRecipeId : null;
    this.onPhase = onPhase || (() => {});
    this.pollIntervalMs = pollIntervalMs || 2000;
    this.timeoutMs = timeoutMs || 30000; // await-idle budget
    // ACK-phase budget: pitch-cooling alone takes minutes on real hardware
    // before the first gate is raised, so this is deliberately generous.
    this.ackTimeoutMs = ackTimeoutMs || 900000;
    // START-accept budget: a healthy keg leaves PROC_IDLE (into pitch-cooling)
    // within seconds of accepting START_PROCESS. If it hasn't begun by then the
    // command was lost or rejected — fail fast instead of waiting out the whole
    // ackTimeout, so the orphan session can be aborted right away.
    this.startTimeoutMs = startTimeoutMs || 45000;
    this.stepDelayMs = stepDelayMs === undefined ? 1500 : stepDelayMs;
    this.phase = PHASES.IDLE;
    this.error = null;
    // Progress flags: which irreversible steps already happened. Persisted in
    // the intent so retries/resume never replay STOP against the aborted
    // session or POST a duplicate new session.
    this._stopped = false;
    this._started = false;
    this._acked = []; // gates ACKed on the CURRENT session
    this._cancelled = false;
    this._intentCreatedAt = null;
    this._panicRetried = false; // one automatic START retry per run (spec §4.4)
  }

  static resumeFromIntent(deviceUuid, options = {}) {
    const raw = localStorage.getItem(intentKey(deviceUuid));
    if (!raw) return null;
    let intent;
    try {
      intent = JSON.parse(raw);
    } catch (e) {
      localStorage.removeItem(intentKey(deviceUuid));
      return null;
    }
    if (Date.now() - intent.createdAt > INTENT_TTL_MS) {
      localStorage.removeItem(intentKey(deviceUuid));
      return null;
    }
    const orch = new FermentationOrchestrator({
      deviceUuid,
      sessionId: intent.sessionId,
      mode: intent.mode,
      settings: intent.settings,
      beerRecipeId: intent.beerRecipeId,
      ...options,
    });
    // Restore progress so run() skips the steps that already happened.
    orch._stopped = intent.stopped || false;
    orch._started = intent.started || false;
    orch._acked = intent.acked || [];
    orch._intentCreatedAt = intent.createdAt; // keep the original TTL anchor
    return orch;
  }

  async run() {
    this.error = null;
    this._cancelled = false;
    // Per-run budget: the modal's manual Retry reuses this same instance
    // (retry = () => this.orchestrator.run()), and each user-initiated
    // attempt earns its own single automatic panic-retry — the flag only
    // guards against looping more than once within one run() call.
    this._panicRetried = false;
    activeOrchestrations.add(this.deviceUuid);
    // _stopped already true at entry means this run is a retry/resume — the
    // swap's STOP happened in a previous attempt.
    const resuming = this._stopped;
    // Last-write on tab death during the vulnerable window (STOP sent, START
    // not yet accepted): fire the START keepalive so the swap can complete
    // without the page. If the page in fact survives, a duplicate session
    // surfaces as the session-conflict 400 the modal already recovers from.
    const pagehidePanic = () => {
      if (this._stopped && !this._started && this.settings) {
        sessionService.startFermentationKeepalive(this.deviceUuid, {
          fermentation_settings: this.settings,
        });
      }
    };
    try {
      window.addEventListener("pagehide", pagehidePanic);
      if (this.mode === "swap" && !this._stopped) {
        // STOP aborts the old session backend-side; from here on the old
        // sessionId is only history — the START below creates a new session.
        this._saveIntent(PHASES.STOPPING);
        this._setPhase(PHASES.STOPPING);
        await this._pause();
        try {
          await sessionService.stopFermentation(this.sessionId);
        } catch (error) {
          // "No active session" means a previous attempt (or the user)
          // already stopped it — that's the state we wanted; continue to
          // await-idle. Anything else is a real failure.
          if (!(await isAlreadyStoppedError(error))) throw error;
        }
        this._stopped = true;
        this._setPhase(PHASES.AWAIT_IDLE);
        this._saveIntent(PHASES.AWAIT_IDLE);
        await this._awaitDevice(
          (d) => d.process_type === PROCESS_TYPES.PROC_IDLE,
          this.timeoutMs,
        );
      }
      if (resuming && !this._started) {
        // STARTING-edge guard: a previous attempt may have died between the
        // session POST resolving and the intent save. Never blind-POST — only
        // start if the keg is verifiably idle right now. If it is fermenting
        // or holding a gate (26/30), a session likely already exists whose id
        // we lost; we cannot ACK without an id, so fail loudly instead of
        // creating a duplicate session or stranding silently.
        const device = await this._fetchDevice();
        if (!device || device.process_type !== PROCESS_TYPES.PROC_IDLE) {
          throw new Error("Keg is not idle — check the device before retrying");
        }
      }
      for (;;) {
        this._setPhase(PHASES.STARTING);
        this._saveIntent(PHASES.STARTING);
        if (!this._started) {
          await this._pause();
          const created = await sessionService.startFermentationSession(
            this.deviceUuid,
            this.beerRecipeId != null
              ? { beer_recipe_id: this.beerRecipeId }
              : { fermentation_settings: this.settings },
          );
          this.sessionId = created.id;
          this._acked = []; // fresh session — no gates ACKed yet
          this._started = true;
        }
        this._saveIntent(PHASES.ACKING);
        this._setPhase(PHASES.ACKING);
        try {
          await this._autoAckUntilSettled();
          break; // settled fermenting
        } catch (error) {
          const startFailed =
            error &&
            (error.startNotAccepted ||
              /EXECUTION_FAILED/.test(error.message || ""));
          // Panic path (spec §4.4): the START leg failed once — abort the orphan
          // session and re-send the SAME settings a single time. Empirically the
          // stranded window is what ruins batches; a lost/refused START frequently
          // succeeds on the immediate retry (same call proven from the ua-38 state
          // in the 2026-08-30 live run).
          if (startFailed && !this._panicRetried && !this._cancelled) {
            this._panicRetried = true;
            // Idle check MUST come before any abort. A stale-gate RESUME NACK
            // (pending_command_error === 3) also matches startFailed even
            // while the keg is genuinely FERMENTING — e.g. a previous
            // attempt's session is alive and progressing. Aborting on sight
            // would STOP that live fermentation before we've confirmed the
            // START actually failed. Only once the device is verifiably idle
            // do we know the session is really an orphan worth aborting.
            const device = await this._fetchDevice();
            if (device && device.process_type === PROCESS_TYPES.PROC_IDLE) {
              await this._abortStartedSession();
              this._started = false;
              this._acked = [];
              // Only track the metric once the retry is actually about to
              // fire — tracking earlier (e.g. right after setting
              // _panicRetried) would record a retry even on the path below
              // that gives up without re-POSTing (device not idle).
              analyticsService.track(ANALYTICS_TYPES.FERMENTATION_PANIC_RETRY, {
                Device: this.deviceUuid,
              });
              continue; // retry the START leg once
            }
            // Not idle (or fetch failed): the session may still be live —
            // leave it and the resume intent untouched, and fail loudly
            // instead of stopping a fermentation that never actually failed.
            throw error;
          }
          if (error && error.startNotAccepted && !this._cancelled) {
            await this._abortStartedSession();
            throw new Error(
              "The keg never started the new fermentation — it may be offline or busy. Please try again.",
            );
          }
          throw error;
        }
      }
      localStorage.removeItem(intentKey(this.deviceUuid));
      this._setPhase(PHASES.DONE);
    } catch (error) {
      if (this._cancelled) {
        // User cancelled: park back to IDLE and keep the intent untouched so
        // the resume banner can pick the operation up later.
        this._setPhase(PHASES.IDLE);
        return;
      }
      this.error = await normalizeError(error);
      this._setPhase(PHASES.ERROR);
    } finally {
      window.removeEventListener("pagehide", pagehidePanic);
      activeOrchestrations.delete(this.deviceUuid);
    }
  }

  // Cooperative cancel: _awaitDevice notices the flag on its next iteration.
  cancel() {
    this._cancelled = true;
  }

  // Recovery for the "device is currently being used by another session" 400:
  // abort ONE (the newest) ACTIVE session on THIS keg, then re-run the swap.
  // One session per call — a second orphan just re-raises the conflict for
  // another click. Scoped strictly to this keg's uuid (attribution via the
  // session's `device` field, the same resolver DELETE/STOP routes through), so
  // it can never abort a session on a different keg.
  // Precondition: only called from the ERROR panel after a session-conflict,
  // which can only arise on the START leg (POST /v1/sessions/). In swap mode
  // that means STOP + await-idle already ran, so `run()` re-enters with
  // `_stopped` already true and resumes at START (no redundant STOP); in start
  // mode there is no STOP branch. So the trailing `run()` never re-stops.
  async resolveConflictAndRetry() {
    const active = await sessionService.listActiveSessions();
    const rows = Array.isArray(active)
      ? active
      : (active && active.results) || [];
    const mine = rows.filter((s) => {
      if (!s) return false;
      const devUuid = s.device && (s.device.uuid || s.device.serial_number);
      const kegUuid = s.keg && (s.keg.uuid || s.keg.serial_number);
      return (
        devUuid === this.deviceUuid ||
        kegUuid === this.deviceUuid ||
        s.device_uuid === this.deviceUuid
      );
    });
    const target = mine.sort((a, b) => b.id - a.id)[0];
    if (target) await sessionService.deleteSession(target.id, true);
    this._stopped = true;
    await this.run();
  }

  // ONE behaviour regardless of start/edit/target/recipe duration: auto-ACK
  // gates the keg raises, one at a time and paced, until it settles
  // fermenting with no pending gate. Only the two prep gates in
  // AUTO_ACK_ALLOWED_GATES — ACTION_PREPARE_FERMENTATION (26) and
  // ACTION_PLACE_TRUB_CONTAINER (30) — are ever ACKed; the keg cannot begin
  // fermenting without them, and the portal user is standing at their own
  // machine. There is no "follow to serving" mode: serving is ~48h away
  // behind a firmware-governed carbonation hold the backend cannot shorten,
  // so the onward serving-handoff gates (mount tap 10, connect pressurizer
  // 31, check carbonation 65, ...) are excluded by the allowlist — not by
  // timing — so this loop can never ACK one even if the keg raises it before
  // (or instead of) a prep gate.
  async _autoAckUntilSettled() {
    // Bound how long the keg may stay idle after START before we treat the
    // start as never-accepted. Independent of sim/device speed: it keys off the
    // device LEAVING idle, not how fast it moves once it has.
    const startDeadline = Date.now() + this.startTimeoutMs;
    let began = false;
    await this._awaitDevice(async (device) => {
      // Fail fast on a hard command rejection. 3 = EXECUTION_FAILED — the keg
      // refused the command. 4 (DELIVERY_FAILED) is transient and retried by the
      // poll loop.
      if (device.pending_command_error === 3) {
        throw new Error(
          "The keg rejected the new fermentation (EXECUTION_FAILED)",
        );
      }

      // Settle check first, so a transient PRIMARY ua:0 in the short path doesn't
      // masquerade as the normal settle.
      const procType = Number(device.process_type);

      // "Began" = the keg actually LEFT PROC_IDLE (into pitch-cooling / fermentation
      // / serving) — the only reliable proof it accepted START_PROCESS. A nonzero
      // user_action is NOT proof: at start the row can still be PROC_IDLE while
      // carrying a STALE gate left from the keg's prior state (observed live:
      // 38/ACTION_KEG_NEEDS_CLEANING from a cleaning prompt), which would falsely
      // mark the start as begun and suppress the never-started fast-fail below. A
      // genuine fermentation gate always arrives with process_type already
      // FERMENTATION, so "left IDLE" covers it without the stale-action false positive.
      if (!began && procType !== PROCESS_TYPES.PROC_IDLE) {
        began = true;
      }
      if (!began && Date.now() > startDeadline) {
        const err = new Error("The keg never started the new fermentation");
        err.startNotAccepted = true;
        throw err;
      }
      // Settled = fermenting, no pending gate, and at least one gate ACKed.
      // The acked check guards the minutes-long pitch-cooling lull (proc=4/ua=0
      // BEFORE gate 26) from being mistaken for the settled state.
      //
      // A 1-minute recipe settles here too. It briefly reaches temperature
      // control, then enters carbonating and raises gate 31 — AFTER we have
      // settled, which is the intent: the serving handoff is a physical job for
      // whoever is at the machine. There is no "follow to serving" mode, because
      // serving is ~48 h away behind a carbonation hold nothing can shorten.
      const isPastPrep =
        device.stage === "Fermenting" ||
        device.stage === "Primary" ||
        device.stage === "Secondary" ||
        device.stage === "Clarification" ||
        device.stage === "Carbonating" ||
        device.stage === "Serving";

      if (
        procType === PROCESS_TYPES.PROC_FERMENTATION &&
        device.user_action === USER_ACTION_IDS.ACTION_UNDEFINED &&
        (this._acked.length > 0 || isPastPrep)
      ) {
        return true;
      }

      // Auto-ACK a pending gate ONLY if it's in AUTO_ACK_ALLOWED_GATES (record
      // BEFORE firing so overlapping polls can't double-ACK; un-record on
      // rejection so the next poll retries). Guard on having left PROC_IDLE
      // first: while the keg is still idle the START hasn't taken effect, so
      // any user_action on the row is a STALE gate carried over from a prior
      // state (observed live: 38/ACTION_KEG_NEEDS_CLEANING). ACKing it fires a
      // RESUME the freshly-started keg was never paused on → firmware
      // EXECUTION_FAILED (errorType 3), surfaced as a spurious "keg rejected the
      // fermentation" even though fermentation actually started fine. Only ACK
      // allowlisted gates the keg raises once it has entered the new process —
      // any other gate (e.g. a stale/leftover 31 from a prior run) is left
      // untouched for a human, regardless of when it's first observed.
      const action = device.user_action;
      if (
        procType !== PROCESS_TYPES.PROC_IDLE &&
        action &&
        AUTO_ACK_ALLOWED_GATES.includes(action) &&
        !this._acked.includes(action)
      ) {
        this._acked.push(action);
        // Pause is inside the predicate; _awaitDevice re-polls right after it
        // resolves. Sequential pacing so checkmarks tick one-by-one.
        await this._pause();
        sessionService.resumeSession(this.sessionId, action).catch(() => {
          const i = this._acked.indexOf(action);
          if (i !== -1) this._acked.splice(i, 1);
        });
      }
      return false;
    }, this.ackTimeoutMs);
  }

  // Abort the session POSTed by this run and clear local progress so it neither
  // strands as an orphan ACTIVE session nor leaves a stale resume intent. Reset
  // _started/sessionId so a subsequent Retry POSTs a fresh session instead of
  // ACKing the dead one. Best-effort: a failed abort must not mask the original
  // error that triggered it.
  async _abortStartedSession() {
    if (!this._started || !this.sessionId) return;
    try {
      await sessionService.stopFermentation(this.sessionId);
    } catch (e) {
      /* already unstartable, or network hiccup — nothing more we can do */
    }
    this._started = false;
    this.sessionId = null;
    this._acked = [];
    localStorage.removeItem(intentKey(this.deviceUuid));
  }

  _pause() {
    if (!this.stepDelayMs || this._cancelled) return Promise.resolve();
    return new Promise((resolve) => setTimeout(resolve, this.stepDelayMs));
  }

  async _awaitDevice(predicate, budgetMs) {
    const deadline = Date.now() + budgetMs;
    const minIntervalMs =
      this.pollIntervalMs != null ? this.pollIntervalMs : 2000;
    let wake = () => {};
    let lastFetchTime = 0;
    const sub = mqttService.notification$.subscribe(() => wake());
    try {
      for (;;) {
        if (this._cancelled) throw new Error("cancelled");

        const elapsed = Date.now() - lastFetchTime;
        if (lastFetchTime > 0 && elapsed < minIntervalMs) {
          await new Promise((resolve) =>
            setTimeout(resolve, minIntervalMs - elapsed),
          );
        }

        lastFetchTime = Date.now();
        const device = await this._fetchDevice();
        if (device && (await predicate(device))) return device;
        if (Date.now() > deadline) {
          throw new Error("Timed out waiting for the keg to respond");
        }
        await new Promise((resolve) => {
          wake = resolve;
          setTimeout(resolve, this.pollIntervalMs);
        });
      }
    } finally {
      sub.unsubscribe();
    }
  }

  async _fetchDevice() {
    const overview = await breweryService.getBreweryInformation();
    const all = [
      ...(overview.fermenting || []),
      ...(overview.serving || []),
      ...(overview.brew_clean_idle || []),
      ...(overview.brew_acid_clean_idle || []),
    ];
    return all.find((d) => d.uuid === this.deviceUuid);
  }

  _saveIntent(phase) {
    // createdAt anchors the TTL; set it once on the first save so repeated
    // saves (or resumed runs) don't keep extending the intent's lifetime.
    this._intentCreatedAt = this._intentCreatedAt || Date.now();
    localStorage.setItem(
      intentKey(this.deviceUuid),
      JSON.stringify({
        sessionId: this.sessionId,
        mode: this.mode,
        settings: this.settings,
        beerRecipeId: this.beerRecipeId,
        phase,
        stopped: this._stopped,
        started: this._started,
        acked: this._acked,
        createdAt: this._intentCreatedAt,
      }),
    );
  }

  _setPhase(phase) {
    this.phase = phase;
    this.onPhase(phase, this.error);
  }
}
